import clsx from "clsx";
import { CContainer } from "@coreui/react-pro";
import { GridLoader } from "react-spinners";
import { useContext, useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { HiArrowNarrowLeft } from "react-icons/hi";
import moment from "moment";

// Hooks
import { useUser } from "../../../core/hooks";
import { useConfiguration } from "../../../core/hooks/db/fisioteduca/useFisioteduca";
import { useIdentityStore, useSnackbarStore } from "../../../core/store";
// Models
import { User } from "../../../core/models/db";
import { Configuration } from "../../../core/models/db/configuration";

// Componentes
import { LoaderContext } from "../../components/loader/context/LoaderContext";
import ConfigurationForm from "./ConfigurationForm";
import { FileService } from "../../components/drag-drop/services/file.service";
import { UploadFile } from "../../components/drag-drop/model/UploadFile";
import { Util } from "../../../core/util/util";
import * as MessageSnackbarDefault from "../../../core/constants/message-snackbar/msgFormDefault";
import { m } from "framer-motion";

const ConfigurationDetail = () => {
    const { t } = useTranslation();
    const navigate = useNavigate();
    // const { showLoader, hideLoader } = useContext(LoaderContext) ?? {};

    // Para obtener el idUser de la identidad
    const { identity } = useIdentityStore((state) => state);

    // -- Hooks de User y Configuration --
    const { getUser, addUser, checkPassword } = useUser();
    const { getConfigurations, addConfiguration } = useConfiguration();

    // -- Estados --
    const [user, setUser] = useState<User | null>(null);
    const [config, setConfig] = useState<Configuration | null>(null);
    const [loading, setLoading] = useState(false);
    const [isEditing, setIsEditing] = useState(false);

    // Para medir altura (opcional)
    const formRef = useRef<HTMLDivElement>(null);
    const [formHeight, setFormHeight] = useState(0);

    const { addSnackbar, setMaxSnackbars } = useSnackbarStore();

    // Efecto para cargar info al inicio
    useEffect(() => {
        (async () => {
            try {
                setLoading(true);

                // Cargamos en paralelo: user (por identity.idUser) y la config
                const [resUser, resConfig] = await Promise.all([
                    getUser({
                        page: 1,
                        itemsPerPage: 1,
                        filters: {
                            id: {
                                value: identity.idUser,
                            },
                        },
                    }),
                    getConfigurations(),
                ]);

                // Manejo del User
                if (resUser?.ok && resUser.item?.rows?.length > 0) {
                    // Tomamos al usuario que corresponde al identity.idUser (seguramente viene en rows[0] si la API regresa uno solo)
                    setUser(resUser.item.rows[0]);
                } else {
                    // Creamos un user ficticio vacío para no romper el formulario
                    setUser({
                        id: identity.idUser || "",
                        email: "",
                        name: "",
                        lastName: "",
                        password: "",
                        image: "",
                        isBlocked: false,
                    });
                }



                // Manejo de Configuration (solo una)
                if (resConfig?.ok && resConfig.item?.rows?.length > 0) {
                    setConfig(resConfig.item.rows[0]);
                } else {
                    // Creamos una config ficticia si no hay ninguna
                    setConfig({
                        id: "",
                        nameTiktok: "",
                        urlTiktok: "",
                        nameInstagram: "",
                        urlInstagram: "",
                        nameFacebook: "",
                        urlFacebook: "",
                        nameTwitter: "",
                        urlTwitter: "",
                        nameYoutube: "",
                        urlYoutube: "",
                    });
                }
            } catch (error) {
                console.error("Error al cargar data", error);
            } finally {
                setLoading(false);
            }
        })();
    }, [identity.idUser]);

    // Para calcular la altura del form (opcional)
    useEffect(() => {
        if (formRef.current) {
            setFormHeight(formRef.current.clientHeight);
        }
    }, [loading, user, config]);

    const handleSubmit = async (updatedUser: User, updatedConfig: Configuration, imageItem: any) => {
        try {
            setLoading(true);

            // Subir la imagen del user si es un blob (igual que en tu Blog)
            // El updatedUser.image puede estar en blob si cambiaste la imagen
            let finalImage = updatedUser.image;

            if (imageItem.image && imageItem.image.startsWith("blob")) {
                const file = await Util.transformBlobToFile(
                    imageItem.image,
                    Math.random() * 1000 + Date.now() + ".png"
                );
                finalImage = await createFile(file, user.id);
                if (imageItem.oldImage && imageItem.oldImage !== finalImage) {
                    await FileService.delete(imageItem.oldImage);
                }
            }
  

            // Realmente actualizamos el user con la imagen final
            const userToSave = { ...updatedUser, image: finalImage };

            // Llama a “addUser” (que dentro hace create o update en tu API).
            const resSaveUser = await addUser(userToSave);
            if (resSaveUser?.ok) {
                setUser(resSaveUser.item);
            }

            // Llama a “addConfiguration”
            const parseInteger = (value) => {
                const num = parseInt(value, 10);
                return isNaN(num) ? null : num;
            };
            
            const resSaveConfig = await addConfiguration({
                ...updatedConfig,
                usersBluesky: parseInteger(updatedConfig.usersBluesky),
                usersFacebook: parseInteger(updatedConfig.usersFacebook),
                usersInstagram: parseInteger(updatedConfig.usersInstagram),
                usersTiktok: parseInteger(updatedConfig.usersTiktok),
                usersTwitter: parseInteger(updatedConfig.usersTwitter),
                usersYoutube: parseInteger(updatedConfig.usersYoutube),
            });
            
            if (resSaveConfig?.ok) {
                setConfig(resSaveConfig.item);
            }

            // Si todo fue bien, salimos de modo edición
            setIsEditing(false);


            // Mensaje de éxito
            addSnackbar(MessageSnackbarDefault.successEditDefault);

        } catch (error) {
            console.error("Error al guardar datos", error);
            addSnackbar(MessageSnackbarDefault.errorDefault);
        } finally {
            setLoading(false);
        }
    };

    /**
     * Subir archivo (igual que en BlogDetail)
     */
    const createFile = async (file: File, idUser: string) => {
        try {
            const { data } = await FileService.add(new UploadFile(file, ["images", "user", idUser]), null);
            if (data.ok) {
                return `/${data.item[0].destination}/${data.item[0].filename}`;
            }
            return undefined;
        } catch (err) {
            throw err;
        }
    };

    const handleCheckPassword = async (id, password) => {
        const result = await checkPassword(id, password);

        if (result && result.ok) {
            return result.item;
        } else {
            return false;
        }
    }

    return (
        <CContainer style={{ display: "flex", flexDirection: "column" }}>
            {/* Encabezado */}
            <div className="w-full justify-between items-center flex pt-4 pb-5">
                <div className="flex flex-col">
                    {/* <div
                        className="flex items-center hover:underline cursor-pointer mb-5"
                        onClick={() => {
                            navigate(-1);
                        }}
                    >
                        <HiArrowNarrowLeft size={20} className="mr-1" />
                        {t("Volver") || "Volver"}
                    </div> */}

                    <span className="text-4xl" style={{ fontWeight: 500 }}>
                        {"Configuración"}
                    </span>
                    <span className="small text-medium-emphasis">
                        {t("Pantalla de edición") || "Pantalla de edición"}
                    </span>
                </div>
            </div>

            {/* Contenido principal */}
            <div className="w-full flex justify-center mb-10">
                <div className="w-full flex justify-center gap-4 flex-col xl:flex-row">
                    <div
                        className={clsx(`flex flex-col w-full justify-center items-center`, {
                            "xl:w-[100%]": !!user?.id,
                        })}
                    >
                        <div className="bg-white block p-4 md:p-7 shadow-md md:rounded-lg w-full md:w-[70%]">
                            <div className="text-2xl font-medium mb-4">
                                {t("Información del Usuario y Redes Sociales") ||
                                    "Información del Usuario y Redes Sociales"}
                            </div>

                            {loading && (
                                <div
                                    className="w-full flex flex-col justify-center items-center"
                                    style={{ height: 400 }}
                                >
                                    <GridLoader color="#3d4f65" size={25} speedMultiplier={0.7} />
                                </div>
                            )}

                            {!loading && user && config && (
                                <div ref={formRef}>
                                    <ConfigurationForm
                                        user={user}
                                        config={config}
                                        isEditing={isEditing}
                                        loading={loading}
                                        onEdit={() => setIsEditing(true)}
                                        onCancel={() => setIsEditing(false)}
                                        checkPassword={handleCheckPassword}
                                        onSubmit={(userData, configData, imageData) =>
                                            handleSubmit(userData, configData, imageData)
                                        }
                                    />
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            </div>
        </CContainer>
    );
};

export default ConfigurationDetail;
